// src/components/forms/AccidentAssessmentForm.tsx
import React, { useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { FORM_STEPS } from "../../types";
import { useForm } from "../hooks/useForm";
import { useLeadTracking } from "../hooks/useLeadTracking";

// Import form step components
import InjuryStep from "./formSteps/InjuryStep";
import RepresentationStep from "./formSteps/RepresentationStep";
import FaultStep from "./formSteps/FaultStep";
import DetailsStep from "./formSteps/DetailsStep";
import WhenStep from "./formSteps/WhenStep";
import FirstNameStep from "./formSteps/FirstNameStep";
import LastNameStep from "./formSteps/LastNameStep";
import EmailStep from "./formSteps/EmailStep";
import PhoneStep from "./formSteps/PhoneStep";
import CompletedStep from "./formSteps/CompletedStep";
import YoutubeStepper from "../stepper";

interface AccidentAssessmentFormProps {
  sourceNumber?: number;
  skipSteps?: string[];
  defaultValues?: {
    physicalInjury?: string;
    treatedBy?: string;
    hasAttorney?: string;
    accidentFault?: string;
  };
}

const AccidentAssessmentForm: React.FC<AccidentAssessmentFormProps> = ({
  sourceNumber,
  skipSteps = [],
  defaultValues = {},
}) => {
  const theme = useTheme();
  const { funnelSource } = useParams<{ funnelSource?: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Initialize form state and lead tracking
  const {
    formData,
    formValidation,
    currentStep,
    isSubmitting,
    submitError,
    handleInputChange,
    handleStepSubmit,
    handleSubmit,
    updateFormWithLeadTracking,
  } = useForm(funnelSource, sourceNumber, defaultValues);

  const { isInitialized, getSubmissionValues } = useLeadTracking();

  // Create a memoized function to get submission values
  const memoizedGetSubmissionValues = useCallback(() => {
    return getSubmissionValues();
  }, [getSubmissionValues]);

  // Update form data with lead tracking information when it becomes available
  useEffect(() => {
    // Only update once when initialized to avoid loops
    if (isInitialized) {
      const values = memoizedGetSubmissionValues();
      // We'll only call this once when isInitialized changes to true
      updateFormWithLeadTracking(values);
    }
  }, [isInitialized, memoizedGetSubmissionValues, updateFormWithLeadTracking]);

  // Create referentially stable handlers for field input and submission
  const handleFieldInput = useCallback(
    (field: string, value: string) => {
      handleInputChange(field, value);
    },
    [handleInputChange]
  );

  const handleFieldSubmit = useCallback(
    (field: string, value: string) => {
      // Always get the latest tracking values when submitting
      const values = getSubmissionValues();
      handleStepSubmit(
        field,
        value,
        values.trustedFormUrl,
        values.jornayaLeadId,
        values.userIp,
        values.zipCode
      );
    },
    [getSubmissionValues, handleStepSubmit]
  );

  // For button selection steps (Yes/No questions)
  const handleButtonSelection = useCallback(
    (field: string, value: string) => {
      // First update the field
      handleInputChange(field, value);

      // Then trigger submission after a brief delay
      setTimeout(() => {
        const values = getSubmissionValues();
        handleStepSubmit(
          field,
          value,
          values.trustedFormUrl,
          values.jornayaLeadId,
          values.userIp,
          values.zipCode,
          values.state
        );
      }, 100);
    },
    [getSubmissionValues, handleInputChange, handleStepSubmit]
  );

  // Handle the custom fault selection (tree/weather)
  const handleTreeFault = () => {
    // Update form data with dequalification
    handleFieldInput("accidentFault", "YES");

    // Get the latest tracking values
    const values = getSubmissionValues();

    // Set custom dequalification reason
    const updatedData = {
      ...formData,
      accidentFault: "YES",
      isDequalified: true,
      dequalReason: "Hit object/no other driver",
      trustedFormUrl: values.trustedFormUrl,
      jornayaLeadId: values.jornayaLeadId,
      userIp: values.userIp,
      zip: values.zipCode,
    };

    // Submit the form
    handleSubmit(updatedData, values.trustedFormUrl, values.jornayaLeadId);
  };

  // Render the appropriate step component
  const renderFormStep = () => {
    // For source 6, don't render the first 3 steps at all
    if (sourceNumber === 6) {
      switch (currentStep) {
        case FORM_STEPS.DETAILS:
          return (
            <DetailsStep
              caseDescription={formData.caseDescription}
              validation={formValidation.caseDescription}
              onChange={handleFieldInput}
              onSubmit={() =>
                handleFieldSubmit("caseDescription", formData.caseDescription)
              }
              isMobile={isMobile}
            />
          );

        case FORM_STEPS.WHEN:
          return (
            <WhenStep
              state={formData.state}
              dateSelection={formData.dateSelection}
              onChange={handleButtonSelection}
              isSubmitting={isSubmitting}
            />
          );

        case FORM_STEPS.FIRST_NAME:
          return (
            <FirstNameStep
              firstName={formData.firstName}
              validation={formValidation.firstName}
              onChange={handleFieldInput}
              onSubmit={() =>
                handleFieldSubmit("firstName", formData.firstName)
              }
              isMobile={isMobile}
            />
          );

        case FORM_STEPS.LAST_NAME:
          return (
            <LastNameStep
              lastName={formData.lastName}
              validation={formValidation.lastName}
              onChange={handleFieldInput}
              onSubmit={() => handleFieldSubmit("lastName", formData.lastName)}
              isMobile={isMobile}
            />
          );

        case FORM_STEPS.EMAIL:
          return (
            <EmailStep
              email={formData.email}
              validation={formValidation.email}
              onChange={handleFieldInput}
              onSubmit={() => handleFieldSubmit("email", formData.email)}
              isMobile={isMobile}
            />
          );

        case FORM_STEPS.PHONE:
          return (
            <PhoneStep
              phone={formData.phone}
              validation={formValidation.phone}
              onChange={handleFieldInput}
              onSubmit={() => handleFieldSubmit("phone", formData.phone)}
              isSubmitting={isSubmitting}
              isMobile={isMobile}
            />
          );

        case FORM_STEPS.COMPLETED:
          return <CompletedStep />;

        default:
          return null;
      }
    }

    // Normal flow for other sources
    switch (currentStep) {
      case FORM_STEPS.INJURY:
        return (
          <InjuryStep
            treatedBy={formData.treatedBy}
            onChange={handleButtonSelection}
          />
        );

      case FORM_STEPS.REPRESENTATION:
        return (
          <RepresentationStep
            hasAttorney={formData.hasAttorney}
            onChange={handleButtonSelection}
          />
        );

      case FORM_STEPS.FAULT:
        return (
          <FaultStep
            accidentFault={formData.accidentFault}
            onChange={handleButtonSelection}
            setDequalified={handleTreeFault}
          />
        );

      case FORM_STEPS.DETAILS:
        return (
          <DetailsStep
            caseDescription={formData.caseDescription}
            validation={formValidation.caseDescription}
            onChange={handleFieldInput}
            onSubmit={() =>
              handleFieldSubmit("caseDescription", formData.caseDescription)
            }
            isMobile={isMobile}
          />
        );

      case FORM_STEPS.WHEN:
        return (
          <WhenStep
            state={formData.state}
            dateSelection={formData.dateSelection}
            onChange={handleButtonSelection}
            isSubmitting={isSubmitting}
          />
        );

      case FORM_STEPS.FIRST_NAME:
        return (
          <FirstNameStep
            firstName={formData.firstName}
            validation={formValidation.firstName}
            onChange={handleFieldInput}
            onSubmit={() => handleFieldSubmit("firstName", formData.firstName)}
            isMobile={isMobile}
          />
        );

      case FORM_STEPS.LAST_NAME:
        return (
          <LastNameStep
            lastName={formData.lastName}
            validation={formValidation.lastName}
            onChange={handleFieldInput}
            onSubmit={() => handleFieldSubmit("lastName", formData.lastName)}
            isMobile={isMobile}
          />
        );

      case FORM_STEPS.EMAIL:
        return (
          <EmailStep
            email={formData.email}
            validation={formValidation.email}
            onChange={handleFieldInput}
            onSubmit={() => handleFieldSubmit("email", formData.email)}
            isMobile={isMobile}
          />
        );

      case FORM_STEPS.PHONE:
        return (
          <PhoneStep
            phone={formData.phone}
            validation={formValidation.phone}
            onChange={handleFieldInput}
            onSubmit={() => handleFieldSubmit("phone", formData.phone)}
            isSubmitting={isSubmitting}
            isMobile={isMobile}
          />
        );

      case FORM_STEPS.COMPLETED:
        return <CompletedStep />;

      default:
        return null;
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      {/* Hidden inputs for tracking */}
      <input name="xxTrustedFormCertUrl" type="hidden" />
      <input name="xxTrustedFormCertUrl1" type="hidden" />
      <input id="leadid_token" name="universal_leadid" type="hidden" value="" />

      {/* Render the current form step */}
      {renderFormStep()}

      {sourceNumber === 2 && <YoutubeStepper currentStep={currentStep} />}

      {/* Loading indicator */}
      {isSubmitting && (
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={32} sx={{ color: "#ff6b35" }} />
        </Box>
      )}

      {/* Error message */}
      {submitError && (
        <Typography color="error" align="center" sx={{ mt: 2 }}>
          {submitError}
        </Typography>
      )}
    </form>
  );
};

export default AccidentAssessmentForm;
